import { useEffect, useState } from 'react';
import { pdf } from '@react-pdf/renderer';
import { message } from 'antd';
import axios from 'axios';
import { getAccessTokenApi } from 'api/auth';
import { API_PATH } from 'config';
import { getOrderConfigApi, getStaticMantencionData } from 'api/budget';
import { getPdfTemplateByTypeApi } from 'api/pdfTemplate';
import { resolveSignatureUrl } from 'api/signatures';
import { resolveDocDesign } from 'PDF/TemplateEngine/resolveDocDesign';
import { deriveTenantColors } from 'PDF/TemplateEngine/deriveTenantColors';
import { buildInformeMantencionDoc } from './buildInformeMantencionDoc';

const useGenerateAndSendPDF = () => {
  const [loading, setLoading] = useState(false);
  // Config histórica para el renderer legacy (MantencionDocument) — se deja
  // intacta para no regresionar ese branch. El branch lowcode usa el tenant
  // completo (getStaticMantencionData) resuelto dentro de generateAndSendPDF,
  // igual que InformeMantencionDownload.jsx.
  const [config, setConfig] = useState({});

  const token = getAccessTokenApi();
  useEffect(() => {
    getOrderConfigApi(token).then((result) => {
      setConfig(result);
    });
  }, [token]);
  const generateAndSendPDF = async (mantencion, staticData, recipients, id) => {
    const { Ascensores, mantencion: mantencionData, actividades } = mantencion;
    setLoading(true);
    // recipients == null → modo automático (envío de sistema, sin toasts).
    // Declarado fuera del try para poder silenciar también el toast de error.
    const auto = recipients == null;
    try {
      const token = getAccessTokenApi();

      // Resolver firmas + config de tenant (colores/logo lowcode) + plantilla
      // lowcode en paralelo. Las firmas se resuelven ANTES de generar el PDF:
      // `pdf().toBlob()` es imperativo y no espera a useEffect async dentro
      // del documento, por eso hay que resolverlas acá.
      const [firmaSpecUrl, firmaRecepUrl, { tenant: tenantConfig }, templateRaw] =
        await Promise.all([
          resolveSignatureUrl(mantencionData?.firma_spec),
          resolveSignatureUrl(mantencionData?.firma_recep),
          getStaticMantencionData(token),
          getPdfTemplateByTypeApi(null, 'informe_mantencion'),
        ]);
      const template = templateRaw?.status === 'client' ? null : templateRaw || null;
      const mantencionWithSignatures = {
        ...mantencionData,
        firma_spec: firmaSpecUrl,
        firma_recep: firmaRecepUrl,
      };

      const design = resolveDocDesign(mantencionWithSignatures, template);
      // Legacy conserva su fuente de config histórica (getOrderConfigApi,
      // state del hook, sin tocar); lowcode usa el tenant completo — mismo
      // criterio que InformeMantencionDownload.jsx — para que el PDF enviado
      // sea idéntico al que se descarga en pantalla.
      const docConfig = design === 'lowcode' ? tenantConfig : config;

      const doc = buildInformeMantencionDoc({
        mantencionPayload: { Ascensores, mantencion: mantencionWithSignatures, actividades },
        template,
        staticData,
        config: docConfig,
        colors: deriveTenantColors(tenantConfig),
        signatures: { firma_spec: firmaSpecUrl, firma_recep: firmaRecepUrl },
      });
      const asPdf = pdf([]);
      asPdf.updateContainer(doc);
      const blob = await asPdf.toBlob();

      // Envía el PDF a la API
      const formData = new FormData();
      formData.append('file', blob, `Informe de mantencion N°${mantencionData.id}.pdf`);
      formData.append('id', mantencionData.id);
      formData.append('to', JSON.stringify(recipients?.to || []));
      formData.append('cc', JSON.stringify(recipients?.cc || []));
      if (auto) formData.append('auto', 'true');
      await axios.post(`${API_PATH}/mantenciones/send-mantencion`, formData, {
        headers: { Authorization: token },
      });
      //   await axios sendMantencionPDF(formData);
      // En modo automático el envío es de sistema en segundo plano: sin toast.
      if (!auto) message.success('Documento enviado exitosamente');
    } catch (error) {
      // El detalle técnico (PDF cliente / backend / relay) va a la consola →
      // Sentry front; al usuario un mensaje simple, sin tecnicismos. El archivado
      // ya no bloquea el envío (best-effort en el backend), así que este error
      // solo ocurre si falla la generación del PDF o el correo en sí.
      console.error(
        '[send-mantencion] fallo al enviar:',
        error?.response?.status,
        error?.response?.data || error
      );
      // Silencioso en modo auto: acción de sistema; en manual sí avisamos.
      if (!auto) message.error('No se pudo enviar el documento. Intenta nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return { generateAndSendPDF, loading };
};

export default useGenerateAndSendPDF;
